import React from 'react';
import ReactDOM from 'react-dom/client';

import App from 'App';
import { Provider } from 'react-redux';
import store from './store';
import '@fontsource/open-sans';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'index.scss';
import * as Sentry from '@sentry/react';

const isLocalHostname = hostname =>
  ['localhost', '127.0.0.1', '::1'].includes(hostname);

const getSentryEnvironment = () => {
  const configuredEnvironment = process.env.REACT_APP_SENTRY_ENVIRONMENT;
  const hostname = window.location.hostname;

  if (isLocalHostname(hostname)) {
    return 'local';
  }

  if (hostname.includes('cinebudget-staging') || hostname.includes('run.app')) {
    return 'staging';
  }

  return configuredEnvironment || 'production';
};

const shouldEnableSentry =
  !isLocalHostname(window.location.hostname) &&
  process.env.REACT_APP_DISABLE_SENTRY !== 'true';

if (shouldEnableSentry) {
  Sentry.init({
    dsn:
      process.env.REACT_APP_SENTRY_DSN ||
      'https://992a9cc3eb8c2c67b9fe79f3099478c2@o4509804793692160.ingest.us.sentry.io/4509916639789056',
    environment: getSentryEnvironment(),
    integrations: [
      // Preserve browser diagnostics while promoting console.error calls to
      // searchable Sentry events in staging and production.
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Ignore errors from cross-origin iframes (like YouTube)
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'NetworkError',
      'Failed to fetch',
    ],
    beforeSend(event, hint) {
      const requestUrl = event?.request?.url || '';
      const errorMessage = hint?.originalException?.message || '';

      // Filter out local development and third-party iframe/ad-blocker noise.
      if (
        requestUrl.includes('localhost') ||
        requestUrl.includes('127.0.0.1') ||
        errorMessage.includes('youtube') ||
        errorMessage.includes('ERR_BLOCKED_BY_CLIENT') ||
        requestUrl.includes('youtube.com') ||
        requestUrl.includes('youtu.be')
      ) {
        return null;
      }

      return event;
    },
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <App />
  </Provider>
);

// Aggressively unregister any existing service workers to prevent OAuth issues
// This must run IMMEDIATELY, before anything else loads
(function () {
  if ('serviceWorker' in navigator) {
    // Unregister all service workers immediately, no questions asked
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister().catch(() => {
          // Silently ignore errors
        });
      });
    });
    // Also unregister the controller if it exists
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
      navigator.serviceWorker.getRegistration().then(registration => {
        if (registration) {
          registration.unregister().catch(() => {
            // Silently ignore errors
          });
        }
      });
    }
  }
})();
